/**
 * Hook for syncing filters and pagination with URL params
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { urlParams } from '@xtnd/web/shared/utils';

interface ReportFilters {
  clients: string[];
  flags?: string[];
}

interface Pagination {
  page: number;
  limit: number;
}

interface UseReportFiltersOptions<T extends ReportFilters> {
  initialFilters?: T;
  initialPagination?: Pagination;
}

export function useReportFilters<T extends ReportFilters>(
  options: UseReportFiltersOptions<T> = {}
) {
  const { initialFilters, initialPagination } = options;
  const [searchParams, setSearchParams] = useSearchParams();

  // Track if we're updating URL to avoid infinite loops
  const isUpdatingUrlRef = useRef(false);

  // Parse URL params into filters and pagination - memoized to avoid recalculation
  const urlState = useMemo(() => {
    const clients = searchParams.getAll('clients');
    const flags = searchParams.getAll('flags');
    const page = searchParams.get('page')
      ? Math.max(1, parseInt(searchParams.get('page')!, 10)) // Ensure page >= 1
      : initialPagination?.page || 1;
    const limit = searchParams.get('limit')
      ? Math.max(1, parseInt(searchParams.get('limit')!, 10)) // Ensure limit >= 1
      : initialPagination?.limit || 100;

    return {
      clients,
      flags: flags.length > 0 ? (flags as T['flags']) : undefined,
      page,
      limit,
    };
  }, [searchParams, initialPagination]);

  // Initialize state from URL params (only on mount)
  const [filters, setFilters] = useState<T>(() => {
    // Parse URL params directly in initializer (only runs once)
    const initialClients = searchParams.getAll('clients');
    const initialFlags = searchParams.getAll('flags');

    return {
      ...initialFilters,
      clients:
        initialClients.length > 0 ? initialClients : initialFilters?.clients || [],
      flags:
        initialFlags.length > 0
          ? (initialFlags as T['flags'])
          : initialFilters?.flags || [],
    } as T;
  });

  const [pagination, setPagination] = useState<Pagination>(() => {
    const page = searchParams.get('page')
      ? Math.max(1, parseInt(searchParams.get('page')!, 10))
      : initialPagination?.page || 1;
    const limit = searchParams.get('limit')
      ? Math.max(1, parseInt(searchParams.get('limit')!, 10))
      : initialPagination?.limit || 100;
    return { page, limit };
  });

  // Sync state when URL params change (but not when we're updating URL ourselves)
  useEffect(() => {
    if (isUpdatingUrlRef.current) {
      isUpdatingUrlRef.current = false;
      return;
    }

    // Only update if values actually changed
    setFilters(prev => {
      const clientsChanged = JSON.stringify(prev.clients) !== JSON.stringify(urlState.clients);
      const flagsChanged = JSON.stringify(prev.flags) !== JSON.stringify(urlState.flags);

      if (!clientsChanged && !flagsChanged) {
        return prev;
      }

      return {
        ...prev,
        clients: urlState.clients.length > 0 ? urlState.clients : prev.clients || [],
        flags: urlState.flags || prev.flags || [],
      } as T;
    });

    setPagination(prev => {
      if (prev.page === urlState.page && prev.limit === urlState.limit) {
        return prev;
      }
      return { page: urlState.page, limit: urlState.limit };
    });
  }, [urlState]);

  /**
   * Update URL params with new filters and/or pagination
   */
  const updateUrlParams = useCallback(
    (newFilters: T, newPagination?: Pagination) => {
      isUpdatingUrlRef.current = true;
      urlParams.updateUrlParams(searchParams, setSearchParams, newFilters, newPagination);
      // Note: searchParams will update via React Router, triggering the sync effect above
    },
    [searchParams, setSearchParams]
  );

  /**
   * Update filters and sync with URL
   */
  const updateFilters = useCallback(
    (newFilters: T, resetPage = true) => {
      const newPagination = resetPage
        ? { page: 1, limit: pagination.limit }
        : pagination;

      // Update state immediately for responsive UI
      setFilters(newFilters);
      if (resetPage) {
        setPagination(newPagination);
      }

      // Update URL (which will sync back via effect, but we skip the sync since we already updated)
      updateUrlParams(newFilters, newPagination);
    },
    [pagination, updateUrlParams]
  );

  /**
   * Update pagination and sync with URL
   */
  const updatePagination = useCallback(
    (newPagination: Pagination) => {
      // Update state immediately for responsive UI
      setPagination(newPagination);

      // Use current filters from state (not closure) to avoid stale values
      updateUrlParams(filters, newPagination);
    },
    [filters, updateUrlParams]
  );

  return {
    filters,
    pagination,
    updateFilters,
    updatePagination,
    updateUrlParams,
  };
}

